import { isPast } from "date-fns/isPast";
import first from "lodash/first";
import isEmpty from "lodash/isEmpty";

import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { RnWebEmbedQuery } from "@/constants/WebView";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

const SurveyPath = "/survey";
const SurveyIdQueryParam = "survey_id";

interface BuildSurveyURLParams {
  queryParams?: Record<string, boolean | number | string>;
  surveyId: string | undefined;
}

const buildSurveyURL = ({
  queryParams,
  surveyId,
}: BuildSurveyURLParams): string | undefined => {
  if (!isNotNilNotEmpty(surveyId)) return;
  const baseUrl = ProcessEnvironment.WebAppURL;
  if (!isNotNilNotEmpty(baseUrl)) return;

  try {
    const url = new URL(SurveyPath, baseUrl);
    url.searchParams.set(SurveyIdQueryParam, surveyId);
    Object.entries(queryParams ?? {}).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });
    if (isWeb()) {
      url.searchParams.set(RnWebEmbedQuery.Key, RnWebEmbedQuery.Value);
    }
    return url.toString();
  } catch {
    return undefined;
  }
};

/** Returns true if there are incomplete baseline assessments */
const hasIncompleteBaselineAssessments = (
  tasks: Record<string, TaskItem[]> | undefined,
) => {
  const allTasks = isEmpty(tasks) ? [] : Object.values(tasks).flat();
  const incompleteBaselineAssessments = allTasks.filter((task) =>
    task.tags?.includes("BASELINE"),
  );
  return incompleteBaselineAssessments.length > 0;
};

const isWhyCompleteAssessmentsAccordionVisible = ({
  appointments: allIntakeAppts,
  tasks,
}: {
  appointments?: AppointmentDetailResponse[];
  tasks?: Record<string, TaskItem[]>;
}) => {
  const isVisible = hasIncompleteBaselineAssessments(tasks);

  // This assumes appointments were sorted by date ascending as to get the patient's first intake appointment correctly.
  const firstIntakeAppt = first(allIntakeAppts);
  if (firstIntakeAppt && isPast(firstIntakeAppt.appointmentEndDateTime)) {
    return false;
  }
  return isVisible;
};

const getAssessmentViewerRole = (isPatient: boolean): "patient" | "support" =>
  isPatient ? "patient" : "support";

export {
  buildSurveyURL,
  getAssessmentViewerRole,
  isWhyCompleteAssessmentsAccordionVisible,
};
